import type { Topic } from "@app/core";
import { createTopic, softDeleteTopic, updateTopic } from "@app/core";
import { create } from "zustand";
import { getServices } from "../platform.js";

interface TopicsState {
  loaded: boolean;
  topics: Topic[];
  hydrate: () => Promise<void>;
  add: (name: string, parentId?: string | null, color?: string | null) => Promise<Topic>;
  rename: (id: string, name: string) => Promise<void>;
  setColor: (id: string, color: string | null) => Promise<void>;
  setIcon: (id: string, icon: string | null) => Promise<void>;
  setParent: (id: string, parentId: string | null) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

// Dedup concurrent hydrate() calls (React StrictMode double-invokes the mount
// effect) so the first-run seed can't insert two sets of default topics in a race.
let inflight: Promise<void> | null = null;

export const useTopicsStore = create<TopicsState>((set, get) => ({
  loaded: false,
  topics: [],

  hydrate() {
    if (inflight) return inflight;
    inflight = hydrateOnce(set).finally(() => { inflight = null; });
    return inflight;
  },

  async add(name, parentId = null, color = null) {
    const { topics } = getServices();
    const t = createTopic({ name, parent_id: parentId, color });
    await topics.insert(t);
    set({ topics: [...get().topics, t] });
    return t;
  },

  async rename(id, name) { await patch(set, get, id, { name }); },
  async setColor(id, color) { await patch(set, get, id, { color }); },
  async setIcon(id, icon) { await patch(set, get, id, { icon }); },
  async setParent(id, parentId) { await patch(set, get, id, { parent_id: parentId }); },

  async remove(id) {
    const { topics } = getServices();
    const current = get().topics.find((t) => t.id === id);
    if (!current) return;
    // Promote direct children one level up (to the removed topic's parent) so the
    // subtree isn't orphaned. Task `topic_ids` keep the now-dangling id — the
    // picker/tree only render resolvable topics, and rewriting every task that
    // referenced it would bump `updated_at` (and future `rev`) on many rows.
    const children = get().topics.filter((t) => t.parent_id === id);
    const moved = new Map<string, Topic>();
    for (const child of children) {
      const m = updateTopic(child, { parent_id: current.parent_id });
      await topics.update(m);
      moved.set(child.id, m);
    }
    await topics.update(softDeleteTopic(current));
    set({
      topics: get().topics.filter((t) => t.id !== id).map((t) => moved.get(t.id) ?? t),
    });
  },
}));

const SEED_ROOTS = [["Work", "💼"], ["School", "📚"], ["Private", "🌿"], ["Health", "🍎"], ["Side Project", "✨"]] as const;
const SEED_CHILDREN = [["Art", "🎨"], ["Furniture", "🪑"]] as const;
/** Default emoji by seed topic name — used to heal icon-less topics that predate
 *  per-topic icons (or lost theirs to the same-name sync dedupe, whose surviving
 *  record is the OLDEST copy and so often the icon-less one). */
const SEED_ICONS: Record<string, string> = Object.fromEntries(
  [...SEED_ROOTS, ...SEED_CHILDREN].map(([name, icon]) => [name.toLowerCase(), icon]),
);

async function hydrateOnce(
  set: (partial: Partial<TopicsState>) => void,
): Promise<void> {
  const { topics } = getServices();
  let list = await topics.listActive();
  // First-run seed: a starter set of groupings, with two children under
  // "Side Project" to demonstrate nesting. Parents are inserted before children
  // so the child `parent_id` resolves.
  if (list.length === 0) {
    const roots: Topic[] = [];
    for (const [name, icon] of SEED_ROOTS) {
      const t = createTopic({ name, icon });
      await topics.insert(t);
      roots.push(t);
    }
    const sideProject = roots.find((t) => t.name === "Side Project")!;
    const children: Topic[] = [];
    for (const [name, icon] of SEED_CHILDREN) {
      const t = createTopic({ name, parent_id: sideProject.id, icon });
      await topics.insert(t);
      children.push(t);
    }
    list = [...roots, ...children];
  } else {
    // Icon backfill: give the seed emoji back to a default-named topic that has
    // none (idempotent; the write bumps updated_at so it also syncs out).
    const healed: Topic[] = [];
    for (const t of list) {
      const icon = t.icon ? null : SEED_ICONS[t.name.trim().toLowerCase()];
      if (!icon) { healed.push(t); continue; }
      const next = updateTopic(t, { icon });
      await topics.update(next);
      healed.push(next);
    }
    list = healed;
  }
  set({ topics: list, loaded: true });
}

async function patch(
  set: (partial: Partial<TopicsState>) => void,
  get: () => TopicsState,
  id: string,
  fields: Parameters<typeof updateTopic>[1],
): Promise<void> {
  const { topics } = getServices();
  const current = get().topics.find((t) => t.id === id);
  if (!current) return;
  const next = updateTopic(current, fields);
  await topics.update(next);
  set({ topics: get().topics.map((t) => (t.id === id ? next : t)) });
}
